import { useEffect, useState } from 'react';
import { CONSENT_CHANGED_EVENT, isTierAllowed } from '@/lib/cookie-consent-core';

/**
 * Reactive wrapper around `isTierAllowed` that re-renders the caller when
 * consent changes. Use this in `useEffect` deps so gated features (Intercom,
 * analytics) initialize as soon as the user clicks Accept, no page reload
 * required.
 *
 * Listens to:
 * - 'cookie-consent-changed' custom event (same-tab changes via setConsentState)
 * - 'storage' event (changes from another tab of the same origin)
 */
export function useIsTierAllowed(tier: 1 | 2 | 3): boolean {
  const [allowed, setAllowed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return isTierAllowed(tier);
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const recheck = () => setAllowed(isTierAllowed(tier));

    window.addEventListener(CONSENT_CHANGED_EVENT, recheck);
    window.addEventListener('storage', recheck);

    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, recheck);
      window.removeEventListener('storage', recheck);
    };
  }, [tier]);

  return allowed;
}
